import type { AxiosError, AxiosInstance } from 'axios';
import { openAccountStatusGateImperative } from '@/store/accountStatusGateStore';
import type { AccountStatusBlockedBody } from './types';

/**
 * Backstop for the server-side account-lockout guard (CP-007b). A
 * `403 { code: 'ACCOUNT_CLOSED' | 'ACCOUNT_LOCKED' }` from any request surfaces
 * the account-blocked screen. There's no replay — the block is server-enforced
 * and can't clear in-session — so the original 403 always propagates.
 *
 * Returns a teardown function (tests / HMR).
 */
export function installAccountStatusInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const body = error.response?.data as AccountStatusBlockedBody | undefined;
      if (error.response?.status === 403 && (body?.code === 'ACCOUNT_CLOSED' || body?.code === 'ACCOUNT_LOCKED')) {
        openAccountStatusGateImperative(body.code);
      }
      throw error;
    },
  );
  return () => instance.interceptors.response.eject(id);
}
